import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { handleApiErrors } from 'helpers';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { SubmitHandler, useForm } from 'react-hook-form';
import { TaskComplexRoleCardProps } from 'types/props/TaskComplexRoleCardProps';

import { ComplexRoleSchema } from 'constants/schemas/ComplexRoleSchema';

import { ComplexRoleUI, RoleUI } from '../types/interfaces';

import '../ui/css/EmployeeCard.css';
import { CustomDropdown } from './custom-controls';
import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';
import { TagsComponent } from './TagsComponent';
interface RefInterface {
  handleCancel: () => void;
}

const TaskComplexRoleCard = React.forwardRef<RefInterface, TaskComplexRoleCardProps>(
  (
    { taskComplexRoleCardProps, onAdd, onUpdate, onDelete, closeDialog, roles }: TaskComplexRoleCardProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      setError,
      formState: { isDirty, errors }
    } = useForm({
      resolver: zodResolver(ComplexRoleSchema),
      defaultValues: {
        ...taskComplexRoleCardProps
      }
    });
    const name = watch('name');
    const roleUIs = watch('roleUIs') || [];
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const toast = useRef<Toast>(null);
    const businessProcessId = watch('id');

    const onSubmit: SubmitHandler<ComplexRoleUI> = useCallback(
      async (data) => {
        const newData = { ...data };
        const dataId = data.id;
        let result;
        const isIdExist =
          data && dataId !== undefined && dataId !== null && dataId !== 0;
        if (onUpdate && isIdExist) {
          result = await onUpdate(dataId, newData);
        }
        if (onAdd && !isIdExist) {
          newData.id = 0;
          result = await onAdd(newData);
        }

        if (result?.data.isSuccess) {
          closeDialog?.();
        } else {
          handleApiErrors({
            error: result?.data.error,
            setError,
            fieldMapping: {
              SameName: {
                field: 'name',
                message:
                  'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название.'
              }
            }
          });

          if (!result?.data.error) {
            toast.current?.show({
              severity: 'error',
              summary: 'Ошибка сохранения',
              detail: 'Не удалось сохранить роль'
            });
          }
        }
      },
      [closeDialog, onAdd, onUpdate, setError]
    );

    const onError = (errors: any) => {
      console.log('Ошибки валидации:', errors);
      if (toast.current) {
        toast.current.show({
          severity: 'error',
          summary: 'Ошибка валидации',
          detail: 'Проверьте заполнение полей',
          life: 3000
        });
      }
    };


    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      } else if (closeDialog) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDataTableDropdownSelectionChange = (e: RoleUI[]) => {
      if (Array.isArray(e)) {
        setValue('roleUIs', e, { shouldDirty: true, shouldValidate: true });
      }
    };

    const executorNodes =
      roleUIs?.length > 0
        ? roleUIs.map((executor) => ({
          key: executor.id.toString(),
          label: executor.name 
        }))
        : [];

    const handleDeleteRoleUi = (roleId: string | number) => {
      if (!roleUIs) return;
      const index = roleUIs.findIndex(
        (role) => role.id.toString() === roleId.toString()
      );

      if (index !== -1) {
        const updatedRoles = [
          ...roleUIs.slice(0, index),
          ...roleUIs.slice(index + 1)
        ];
        setValue('roleUIs', updatedRoles, { 
          shouldDirty: true, 
          shouldValidate: true 
        });
      }
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
    };

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = () => {
      if (onDelete) {
        onDelete(businessProcessId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    return (
      <div className="dialog-tabless-card complexRole-card__roles" style={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Toast ref={toast} />
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden'
        }}>
          <Divider />
          <div style={{ 
            flex: 1,
            display: 'grid',
            gridTemplateRows: 'auto auto auto 1fr auto', 
            minHeight: 0,
            overflow: 'hidden'
          }}>

            <div className="flex flex-row gap-5">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="name" className="col p-0">
                    Название<span className="required">*</span>:
                  </label>
                  <div className="col-8 p-0">
                    <InputText
                      className="user-card__input w-full"
                      {...register('name')}
                    />
                  </div>
                </div>
                <p
                  className={`task-status-card-errors card-errors ${
                    errors.name ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.name ? errors.name.message : ' '}
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="description" className="col p-0">
                    Описание:
                  </label>
                  <div className="col-8 p-0">
                    <InputText
                      className="user-card__input w-full"
                      {...register('description')}
                    />
                  </div>
                </div>
                <p
                  className={`task-status-card-errors card-errors ${
                    errors.description ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.description ? errors.description.message : ' '}
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="description" className="col p-0">
                    Набор прав:
                  </label>
                  <div className="col-8 p-0">
                    <CustomDropdown<RoleUI>
                      key="roles"
                      multiplySelectMode
                      assetOptions={roles}
                      selectedAsset={roleUIs}
                      onSelectionChange={(e) => handleDataTableDropdownSelectionChange(e as RoleUI[])}
                      placeholder={
                        roleUIs?.length > 0
                          ? `Выбрано микро-ролей: ${roleUIs?.length}`
                          : 'Выбор микро-ролей'
                      }
                      columns={[
                        {
                          field: 'name',
                          header: 'Название',
                          width: '33%'
                        },
                        { field: 'displayName', header: 'Полное название', width: '33%' },
                        {
                          field: 'description',
                          header: 'Описание',
                          width: '33%'
                        }
                      ]}
                      emptyMessage={'Микро-роли не найдены'}
                      overlayWidthMultiplier={1}
                      filterWithLabel
                      minWidth={650}
                      customHeight={360}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={{
              gridRow: '4',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
              marginTop: 15
            }}>
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                color: '#6c757d',
                border: '1px solid rgb(211, 211, 211)',
                borderRadius: '6px',
                overflow: 'hidden',
                minHeight: 0
              }}>
                <div style={{ 
                  flex: 1,
                  overflowY: 'auto',
                  padding: '10px',
                  minHeight: 0
                }}>
                  {roleUIs.length === 0 && (
                    <span style={{ marginLeft: '8px', color: '#6c757d' }}>
                      Микро-роли не выбраны
                    </span>
                  )}
                  <TagsComponent
                    initialTagsItems={executorNodes}
                    onDelete={handleDeleteRoleUi}
                  />
                </div>
              </div>
            </div>

            <div style={{ 
              gridRow: '5',
              paddingTop: '1rem',
              flexShrink: 0
            }}>
              <div className="default-card__buttons">
                <div className="flex gap-3">
                  <Button
                    label="Сохранить"
                    icon="pi pi-check"
                    onClick={handleSubmit(onSubmit, onError)}
                  />
                  <Button
                    label="Удалить"
                    icon="pi pi-trash"
                    onClick={handleDelete}
                    className="p-button-danger"
                  />
                  <Button
                    label="Отмена"
                    icon="pi pi-times"
                    onClick={handleCancel}
                    className="p-button-secondary"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="роль"
          entityName={name}
        />

      </div>
    );
  }
);

TaskComplexRoleCard.displayName = 'TaskComplexRoleCard';

export default TaskComplexRoleCard;
